import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';

import { AnalyticsService, PartnerBrandService } from '@campfire-nx/api';

import { AuthService } from '@open-portal-app/core/services/auth.service';
import { OdoReadingService } from './odo-reading.service';

import {
  ERROR_ON_SAVE_INFO_BOX,
  ODO_READING_EXISTS_FLOW_STEP_ONE,
  ODO_READING_FLOW_STEP_ONE,
  ODO_READING_FLOW_STEP_TWO
} from '../constants/odo-flow-v001';

@Injectable({
  providedIn: 'root'
})
export class OdoFlowManagerService {
  steps = [] as any;
  currentStepIndex = 0;
  isAuthenticated!: boolean;
  partnerKey!: string;
  policySlug!: string;
  carPolicy!: any;
  error!: any;

  constructor(
    private router: Router,
    private analyticsService: AnalyticsService,
    private odoReadingService: OdoReadingService,
    private authService: AuthService,
    private partnerBrandService: PartnerBrandService,
    private decimalPipe: DecimalPipe
  ) { }

  getFirstStep(policy: { [key: string]: any }, policySlug: string) {
    this.carPolicy = policy;
    this.policySlug = policySlug;
    this.isAuthenticated = this.authService.isAuthenticated();
    this.partnerKey = this.carPolicy.partner?.api_key || this.carPolicy.error?.partner?.api_key;
    const queryParams = this.router.routerState.snapshot.root.queryParams;

    if (
      this.carPolicy &&
      this.carPolicy.error &&
      this.carPolicy.status === 409
    ) {
      this.steps = [ODO_READING_EXISTS_FLOW_STEP_ONE];
      this.steps[0].analytics = {
        ...this.steps[0].analytics,
        policy_slug: this.policySlug
      };

      // A delay to ensure partner branding is applied to check Auth0
      setTimeout(() => {
        // Hide CTA button if Auth0 is disabled
        if (!this.partnerBrandService.getAttribute('isAuth0Enabled', 'partner')) {
          this.steps[0].settings.hide_cta = true;
        };
      }, 500);

      this.analyticsService.trackEvent('Initialise', 'odoReadingExistsFlow', 'start-flow', this.steps[0].analytics);
    } else {
      const firstStep = JSON.parse(JSON.stringify(ODO_READING_FLOW_STEP_ONE));
      const firstParagraph =
        `<p>Please share the current odometer reading for your ${this.carPolicy.car_year} ${this.carPolicy.car_maker} ${this.carPolicy.car_model}.</p>`;

      firstStep.sections[0].subtext = firstParagraph + ODO_READING_FLOW_STEP_ONE.sections[0].subtext;
      firstStep.analytics = {
        ...firstStep.analytics,
        policy_slug: this.policySlug
      };

      // Add back button to the page if coming from the SupportFlow
      if (queryParams && queryParams.supportFlow === 'true') {
        firstStep.actions = { back: {} };
      }

      this.steps = [firstStep, ODO_READING_FLOW_STEP_TWO];

      this.analyticsService.trackEvent('Initialise', 'odoReadingFlow', 'start-flow', this.steps[0].analytics);
    };

    return this.steps[0];
  }

  next(payload: any) {
    return new Promise((resolve) => {
      if (
        this.currentStepIndex === 0 &&
        this.steps[this.currentStepIndex].flow_slug === 'odoReadingFlow'
      ) {
        this.submitOdoReading(payload.reading, resolve);
      } else {
        if (this.isAuthenticated) {
          this.router.navigate([`/policy/car/${this.policySlug}`]);
        } else {
          this.router.navigateByUrl(`/login?partner=${encodeURIComponent(this.partnerKey)}&action=policy&ref=${this.policySlug}&entity=car`);
        };
      };
    });
  }

  back() {
    return new Promise((resolve) => {
      if (this.currentStepIndex === 0) {
        this.router.navigate(['/support']);
      }

      resolve('');
    })
  }

  submitOdoReading(reading: string, resolve: (value: unknown) => void) {
    const parsedReading = parseInt(reading, 10);

    this.odoReadingService.create(this.policySlug, parsedReading)
      .then((response: any) => {
        const lastStep = JSON.parse(JSON.stringify(ODO_READING_FLOW_STEP_TWO));
        const paydKms = response.end_odo - response.start_odo;

        lastStep.sections[0].info.content =
          lastStep.sections[0].info.content.replace(/{{start_odo}}/g, this.formatNumber(response.start_odo));
        lastStep.sections[0].info.content =
          lastStep.sections[0].info.content.replace(/{{payd_kms}}/g, this.formatNumber(paydKms));
        lastStep.sections[0].info.content =
          lastStep.sections[0].info.content.replace(/{{end_odo}}/g, this.formatNumber(response.end_odo));

        lastStep.analytics = {
          ...lastStep.analytics,
          policy_slug: this.policySlug
        };

        // hide CTA if not Auth0 enabled or not logged in
        if (
          !this.isAuthenticated &&
          !this.partnerBrandService.getAttribute('isAuth0Enabled', 'partner')
        ) {
          lastStep.settings.hide_cta = true;
        }

        this.steps[this.steps.length - 1] = lastStep;
        this.currentStepIndex++;

        this.analyticsService.trackEvent(
          'SubmitOdoReading',
          'odoReadingFlow',
          'success',
          this.steps[this.currentStepIndex].analytics
        );

        resolve(this.steps[this.currentStepIndex]);
      })
      .catch((error) => {
        this.steps[0].sections[0].info = ERROR_ON_SAVE_INFO_BOX;

        this.analyticsService.trackEvent(
          'SubmitOdoReading',
          'odoReadingFlow',
          'error',
          this.steps[this.currentStepIndex].analytics,
          { error: error.code }
        );

        resolve(this.steps[0]);
      });
  };

  formatNumber(num: number) {
    return this.decimalPipe.transform(num, '1.0-0');
  };
};
